import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import { MessagesSquare, Sparkles } from 'lucide-react';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useAiStatus,
  useAskStreaming,
  useChatThread,
  useChatThreads,
  useDeleteThread,
  useSeasons,
  useTeams,
} from '@/app/queries';
import { Button, Spinner } from '@/ui/Button';
import { Notice, PageHeader, Panel } from '@/ui/Panel';
import { AiRefusal } from '@/ui/AiAnswer';
import { ChatTurn } from '@/routes/chat/ChatTurn';
import { Composer } from '@/routes/chat/Composer';
import { ThreadDrawer, ThreadRail } from '@/routes/chat/ThreadRail';
import { useFillHeight } from '@/routes/chat/useFillHeight';

/** Four questions the app can actually answer, as a way in rather than a blank box. */
const SUGGESTIONS = ['s1', 's2', 's3', 's4'] as const;

/**
 * Asking about your own club.
 *
 * The one AI surface whose cost grows by itself — every turn resends the
 * conversation — so three things are visible rather than buried: what the last
 * answer cost, roughly how many more questions the balance is worth, and when
 * the thread got long enough that the oldest turns stopped being sent.
 *
 * A coach who can see the meter running trusts it. One who finds out later
 * does not.
 *
 * It is laid out as a conversation and not as a page: the transcript scrolls
 * inside a region that reaches the bottom of the screen, and the composer is
 * pinned under it. As a document it had the box you type into below everything
 * you had already asked, which gets further away the more you use it.
 */
export function Chat({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const status = useAiStatus();
  const teams = useTeams();
  const threads = useChatThreads();
  const ask = useAskStreaming();
  const remove = useDeleteThread();

  const [threadId, setThreadId] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const [drawer, setDrawer] = useState(false);
  /** The question being answered, shown immediately rather than after the turn. */
  const [asked, setAsked] = useState<string | null>(null);
  const thread = useChatThread(threadId);

  const frame = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const height = useFillHeight(frame);

  const teamId = teams.data?.[0]?.id ?? null;
  const seasons = useSeasons(teamId ?? undefined);
  const seasonId = seasons.data?.[0]?.id ?? null;
  const messages = thread.data?.messages ?? [];
  const last = ask.reply;

  /*
    The transcript scrolls, not the window — scrolling the window here used to
    fight the page it was inside.

    It follows the answer while it is written, but only while the coach is
    already at the bottom. Dragging a stream back down every time a word
    arrives is the behaviour that makes an assistant impossible to read back
    mid-answer, and every one worth copying stopped doing it years ago.
  */
  const [atBottom, setAtBottom] = useState(true);
  useEffect(() => {
    const el = scroller.current;
    if (!el || !atBottom) return;
    el.scrollTo({ top: el.scrollHeight, behavior: ask.streaming ? 'auto' : 'smooth' });
  }, [messages.length, ask.pending, ask.streaming, atBottom]);

  const send = async (text?: string) => {
    const q = (text ?? question).trim();
    if (q.length < 2) return;
    setQuestion('');
    setAsked(q);
    setAtBottom(true);
    const reply = await ask.ask({ question: q, threadId: threadId ?? undefined, teamId });
    setAsked(null);
    if (reply) setThreadId(reply.threadId);
  };

  const startNew = () => {
    setThreadId(null);
    setQuestion('');
    setAsked(null);
    ask.reset();
    setDrawer(false);
  };

  if (status.data && (!status.data.enabled || !status.data.inPlan)) {
    return (
      <>
        <PageHeader
          title={t('chat.title')}
          lead={t('chat.lead')}
          help={localePath(locale, '/guide/ai')}
        />
        <Panel title={t('chat.title')}>
          <div className="p-4">
            <Notice>
              {status.data.inPlan
                ? t('ai.refused.ai_switched_off')
                : t('ai.refused.ai_not_in_plan')}
            </Notice>
          </div>
        </Panel>
      </>
    );
  }

  const refusal = ask.error;
  const threadList = threads.data ?? [];
  const title = thread.data?.thread.title;

  return (
    <>
      {/* On a phone the conversation is the screen, and a title, a lead and a
          help link above it took four hundred pixels of the eight hundred
          there are — so the transcript opened showing one suggestion and half
          of the next. The conversation's own header carries the title there;
          this is for the desktop, where there is room for both. */}
      <div className="hidden lg:block">
        <PageHeader
          title={t('chat.title')}
          lead={t('chat.lead')}
          help={localePath(locale, '/guide/ai')}
        />
      </div>

      <div
        ref={frame}
        style={{ height }}
        className="grid min-h-[26rem] grid-cols-1 gap-5 lg:grid-cols-[17rem_1fr]"
      >
        <ThreadRail
          threads={threadList}
          activeId={threadId}
          locale={locale}
          onOpen={(id) => {
            setThreadId(id);
            ask.reset();
          }}
          onNew={startNew}
          onDelete={async (id) => {
            await remove.mutateAsync(id);
            if (id === threadId) setThreadId(null);
          }}
        />

        <section className="flex min-h-0 flex-col rounded-(--radius-panel) border border-line bg-surface shadow-(--shadow-panel)">
          <header className="flex items-center gap-2 border-b border-line px-3 py-2.5 sm:px-4">
            {/* The way back to the list on a screen that only has room for one
                of the two. */}
            <Button
              variant="quiet"
              className="!min-w-11 !px-2 lg:!hidden"
              onClick={() => setDrawer(true)}
            >
              <MessagesSquare aria-hidden="true" className="size-4" />
              <span className="sr-only sm:not-sr-only">{t('chat.threads')}</span>
            </Button>
            {/*
              Before anything has been asked, this says the screen's name on a
              phone — where it is the only heading there is — and "New" on a
              desktop, where the page header has already said it. One heading
              per screen, whichever screen you are on.
            */}
            <h2 className="min-w-0 flex-1 truncate font-condensed text-subhead font-semibold tracking-tight">
              {title ?? (
                <>
                  <span className="lg:hidden">{t('chat.title')}</span>
                  <span className="hidden lg:inline">{t('chat.newThread')}</span>
                </>
              )}
            </h2>
            {status.data ? (
              <span className="tabular shrink-0 text-meta text-ink-faint">
                {t('ai.balanceLeft', { count: last?.balance ?? status.data.credits })}
              </span>
            ) : null}
          </header>

          <div
            ref={scroller}
            onScroll={(e) => {
              const el = e.currentTarget;
              setAtBottom(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
            }}
            role="log"
            aria-live="polite"
            aria-label={t('chat.title')}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-5 sm:px-5"
          >
            <div className="mx-auto grid max-w-[72ch] gap-6">
              {messages.length === 0 && !ask.pending && !asked ? (
                <div className="py-6">
                  <p className="flex items-center gap-2 font-condensed text-headline font-semibold tracking-tight">
                    <Sparkles aria-hidden="true" className="size-5 text-signal" />
                    {t('chat.askAnything')}
                  </p>
                  <p className="mt-2 max-w-[56ch] text-ink-muted">{t('chat.askAnythingBody')}</p>
                  {/* The help link lives in the page header on a desktop; on a
                      phone there is no page header, and this is the moment a
                      coach wants it. */}
                  <p className="mt-2 lg:hidden">
                    <Link
                      to={localePath(locale, '/guide/ai')}
                      className="inline-flex min-h-11 items-center text-meta underline decoration-line-strong underline-offset-2 hover:decoration-ink"
                    >
                      {t('common.whatIsThisFor')}
                    </Link>
                  </p>

                  {/* A blank box asks a coach to invent the question. These are
                      four the app can actually answer, and tapping one is the
                      shortest route from "what is this" to seeing it work. */}
                  <p className="mt-6 text-meta font-semibold text-ink-muted">
                    {t('chat.tryOne')}
                  </p>
                  <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                    {SUGGESTIONS.map((key) => (
                      <li key={key}>
                        <button
                          type="button"
                          onClick={() => void send(t(`chat.suggest.${key}`))}
                          className="min-h-11 w-full rounded-(--radius-control) border border-line bg-surface-sunken px-3 py-2.5 text-start leading-snug transition-colors hover:border-ink hover:bg-surface"
                        >
                          {t(`chat.suggest.${key}`)}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {messages.map((m) => (
                <ChatTurn key={m.id} message={m} teamId={teamId} seasonId={seasonId} />
              ))}

              {/* The question appears the moment it is asked, not when the
                  answer lands. Waiting for the round trip to show your own
                  words is the thing that makes a chat feel like a form. */}
              {asked ? (
                <div className="flex animate-rise justify-end">
                  <p className="max-w-[46ch] whitespace-pre-wrap rounded-(--radius-panel) bg-ink px-4 py-2.5 leading-relaxed text-paper">
                    {asked}
                  </p>
                </div>
              ) : null}

              {ask.pending || ask.streaming ? (
                <div className="max-w-[68ch] animate-rise">
                  <p className="mb-1.5 flex items-center gap-1.5 text-meta font-semibold text-ink-muted">
                    <Sparkles aria-hidden="true" className="size-3.5" />
                    {t('chat.assistant')}
                  </p>
                  {ask.streaming ? (
                    <div className="whitespace-pre-wrap leading-relaxed">
                      {ask.streaming}
                      {/* The caret is the only thing on screen saying the
                          answer is still being written. */}
                      <span className="ms-0.5 inline-block h-[1em] w-[2px] animate-blink align-text-bottom bg-ink-faint" />
                    </div>
                  ) : (
                    <p className="flex items-center gap-2 text-meta text-ink-muted">
                      <Spinner /> {t('chat.thinking')}
                    </p>
                  )}
                </div>
              ) : null}

              {refusal?.code ? <AiRefusal code={refusal.code} /> : null}

              {/* Said plainly when it happens: the start of a long thread stops
                  being sent, and a coach who does not know that will think the
                  assistant forgot. */}
              {last && last.droppedTurns > 0 ? (
                <Notice>{t('chat.trimmed', { count: last.droppedTurns })}</Notice>
              ) : null}
            </div>
          </div>

          <div className="mx-auto w-full max-w-[72ch] sm:px-2">
            <Composer
              value={question}
              onChange={setQuestion}
              onSend={() => void send()}
              onStop={ask.stop}
              busy={ask.pending}
              hint={
                last
                  ? `${t('chat.costNote')} ${t('chat.messagesLeft', { count: last.messagesLeft })}`
                  : t('chat.costNote')
              }
            />
          </div>
        </section>
      </div>

      <ThreadDrawer
        open={drawer}
        onClose={() => setDrawer(false)}
        threads={threadList}
        activeId={threadId}
        locale={locale}
        onOpen={(id) => {
          setThreadId(id);
          ask.reset();
          setDrawer(false);
        }}
        onNew={startNew}
        onDelete={async (id) => {
          await remove.mutateAsync(id);
          if (id === threadId) setThreadId(null);
        }}
      />
    </>
  );
}
